import React, { useReducer, useEffect, useState } from 'react';
import './styles/globals.css';
import { loadStore, saveStore, getDefaultState } from './utils/store';
import Shell from './components/Shell';
import Dashboard from './pages/Dashboard';
import Plants from './pages/Plants';
import TreeMap from './pages/TreeMap';
import Costs from './pages/Costs';
import Tasks from './pages/Tasks';
import Journal from './pages/Journal';
import Settings from './pages/Settings';

function reducer(state, action) {
  switch (action.type) {
    case 'ADD_PLANT':
      return { ...state, plants: [...state.plants, action.plant] };
    case 'UPDATE_PLANT':
      return { ...state, plants: state.plants.map(p => p.id === action.plant.id ? action.plant : p) };
    case 'DELETE_PLANT':
      return { ...state, plants: state.plants.filter(p => p.id !== action.id) };

    case 'ADD_TASK':
      return { ...state, tasks: [...(state.tasks || []), action.task] };
    case 'TOGGLE_TASK':
      return { ...state, tasks: state.tasks.map(t => t.id === action.id ? { ...t, done: !t.done } : t) };
    case 'DELETE_TASK':
      return { ...state, tasks: state.tasks.filter(t => t.id !== action.id) };

    case 'ADD_COST':
      return { ...state, costs: [...(state.costs || []), action.cost] };
    case 'UPDATE_COST':
      return { ...state, costs: state.costs.map(c => c.id === action.cost.id ? action.cost : c) };
    case 'DELETE_COST':
      return { ...state, costs: state.costs.filter(c => c.id !== action.id) };

    case 'ADD_JOURNAL':
      return { ...state, journalEntries: [...(state.journalEntries || []), action.entry] };
    case 'DELETE_JOURNAL':
      return { ...state, journalEntries: state.journalEntries.filter(e => e.id !== action.id) };

    case 'ADD_TREE_MARKER':
      return { ...state, treeMarkers: [...(state.treeMarkers || []), action.marker] };
    case 'UPDATE_TREE_MARKER':
      return { ...state, treeMarkers: state.treeMarkers.map(m => m.id === action.id ? { ...m, ...action.updates } : m) };
    case 'DELETE_TREE_MARKER':
      return { ...state, treeMarkers: state.treeMarkers.filter(m => m.id !== action.id) };
    case 'SET_TREE_MAP_BG':
      return { ...state, treeMapBackground: action.url };

    case 'ADD_MEMBER':
      return { ...state, members: [...(state.members || []), action.member] };

    case 'IMPORT_DATA':
      return { ...state, ...action.data };

    default:
      return state;
  }
}

export default function App() {
  const [page, setPage] = React.useState('dashboard');
  const [state, dispatch] = useReducer(reducer, getDefaultState());
  const [loaded, setLoaded] = useState(false);

  // Data now lives in Supabase (shared across everyone/every device), with
  // localStorage kept only as an offline-ish fallback cache — see store.js.
  useEffect(() => {
    let cancelled = false;
    loadStore().then(initial => {
      if (!cancelled) {
        dispatch({ type: 'IMPORT_DATA', data: initial });
        setLoaded(true);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Don't persist anything until the real data has finished loading —
  // otherwise we'd briefly overwrite Supabase with the empty default state.
  useEffect(() => {
    if (loaded) saveStore(state);
  }, [state, loaded]);

  if (!loaded) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', fontFamily: 'var(--font-body, sans-serif)',
        color: 'var(--text-secondary, #666)', fontSize: 14,
      }}>
        Loading your garden…
      </div>
    );
  }

  const pages = {
    dashboard: Dashboard,
    plants: Plants,
    treemap: TreeMap,
    costs: Costs,
    tasks: Tasks,
    journal: Journal,
    settings: Settings,
  };
  const PageComponent = pages[page] || Dashboard;

  return (
    <Shell page={page} setPage={setPage} members={state.members}>
      <PageComponent state={state} dispatch={dispatch} setPage={setPage} />
    </Shell>
  );
}
